import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MatStepper,
  MAT_DIALOG_DATA
} from '@angular/material';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { defer, merge, Observable, of } from 'rxjs';
import { delay, shareReplay } from 'rxjs/operators';
import { CSVPatientsFormGroup } from './import-wizard-file-received/csv-patients-form-group.model';

export type PatientImportWizardMatDialogRef = MatDialogRef<PatientImportWizardModalComponent>;

@Component({
  selector: 'csi-patient-import-wizard-modal',
  templateUrl: './patient-import-wizard-modal.component.html',
  styleUrls: ['./patient-import-wizard-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientImportWizardModalComponent implements OnDestroy, AfterViewInit {
  public readonly fileControl: FormControl;
  public readonly fileChanges$: Observable<File[]>;
  public csvPatientsFormGroup: CSVPatientsFormGroup;
  public isEditable = true;
  public isMassClinic: boolean;
  // Imports with flagged rows get a dedicated "Review Flagged Rows" step between CSV-preview and
  // check-existing. While it is visible every later step shifts one position to the right, so the
  // indices below must be derived, never hardcoded.
  public isFlaggedRowsStepVisible = false;
  // The stepper is non-linear, and Material lets header clicks jump FORWARD regardless of a
  // step's [completed] state — which would skip validation and the flagged-row review entirely.
  // Forward moves are only legal when this component initiates them via advanceToStep.
  private isProgrammaticStepAdvance = false;
  @ViewChild('stepper', { static: true }) matStepper: MatStepper;

  public get checkExistingPatientsStepIndex(): number {
    return this.isFlaggedRowsStepVisible ? 3 : 2;
  }

  // Index of whichever step follows check-existing: mass-clinic import or send-questionnaires.
  public get stepIndexAfterCheckExisting(): number {
    return this.checkExistingPatientsStepIndex + 1;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public patientImportData: {
      importedFile?: File;
      isMassClinic?: boolean;
    },
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private matDialogService: MatDialog
  ) {
    this.fileControl = this.fb.control(
      this.patientImportData.importedFile !== null ? [this.patientImportData.importedFile] : null,
      Validators.required
    );
    this.fileChanges$ = merge(
      this.fileControl.valueChanges,
      defer(() => of(this.fileControl.value))
    ).pipe(shareReplay(1));

    this.fileControl.valueChanges.pipe(delay(0), untilDestroyed(this)).subscribe(() => {
      if (this.fileControl.valid) {
        this.advanceToStep(1);
      }
      this.isFlaggedRowsStepVisible = false;
      this.changeDetectorRef.markForCheck();
      this.csvPatientsFormGroup = new CSVPatientsFormGroup();
    });
    this.csvPatientsFormGroup = new CSVPatientsFormGroup();
  }

  ngOnDestroy() {}

  ngAfterViewInit() {
    if (this.patientImportData.importedFile !== null) {
      this.allowUserToContinueToNextStep();
    }
    this.matStepper.selectionChange.pipe(untilDestroyed(this)).subscribe(stepChange => {
      const isForwardJump = stepChange.selectedIndex > stepChange.previouslySelectedIndex;
      if (isForwardJump && !this.isProgrammaticStepAdvance) {
        // A header click jumped forward past validation — bounce back where the user was.
        setTimeout(() => {
          this.matStepper.selectedIndex = stepChange.previouslySelectedIndex;
          this.changeDetectorRef.markForCheck();
        });
      }
    });
  }

  public static open(
    matDialogService: MatDialog,
    importedFile: File = null,
    isMassClinic?: boolean,
    modalConfig: MatDialogConfig<never> = {}
  ): PatientImportWizardMatDialogRef {
    return matDialogService.open<PatientImportWizardModalComponent>(
      PatientImportWizardModalComponent,
      {
        maxHeight: '90vh',
        minWidth: '60vw',
        disableClose: true,
        data: { importedFile, isMassClinic },
        ...modalConfig
      }
    );
  }

  protected goBack() {
    this.matStepper.selectedIndex = this.matStepper.selectedIndex - 1;
  }

  public goNext() {
    this.advanceToStep(this.matStepper.selectedIndex + 1);
  }

  protected toSendQuestionnaires() {
    this.advanceToStep(this.stepIndexAfterCheckExisting);
    setTimeout(() => this.advanceToStep(this.stepIndexAfterCheckExisting), 10000);
    this.isEditable = false;
  }

  // Material emits selectionChange synchronously inside the selectedIndex setter, so the flag
  // is consumed by the guard in ngAfterViewInit before it is cleared here.
  private advanceToStep(stepIndex: number) {
    this.isProgrammaticStepAdvance = true;
    this.matStepper.selectedIndex = stepIndex;
    this.isProgrammaticStepAdvance = false;
  }

  // The CSV-preview step revalidated clean: remove any review step left over from an earlier
  // attempt so the steps that follow return to their expected positions, then advance.
  public onCsvPreviewPassedValidation() {
    this.isFlaggedRowsStepVisible = false;
    this.goNext();
  }

  // Validation found flagged rows: reveal the dedicated review step, wait a tick for it to
  // render, then step into it.
  public onCsvPreviewFlaggedRows() {
    this.isFlaggedRowsStepVisible = true;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => this.goNext());
  }

  public exitModal() {
    this.matDialogService.closeAll();
  }

  protected endImportProcess() {
    window.location.reload();
  }
  private allowUserToContinueToNextStep() {
    // Forces the first step to be interacted, so that mat-step allows the user to go to the next step
    this.matStepper._steps.toArray()[0].interacted = true;
  }
}
